import { PAY_OUT_LABELS, describeOverShort } from '@didto/domain';
import type { PayOutCategory } from '@didto/domain';
import {
  Badge,
  Button,
  EmptyState,
  IconButton,
  KvRow,
  Loading,
  Notice,
  PageHeader,
  SectionHead,
  Surface,
} from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useOwnerReview } from '@/app/ownerReview';
import { useToast } from '@/app/toast';
import { useOnline } from '@/lib/network';
import { CLOSE_KIND_LABEL, addDays, fetchDayView, reviewKeys } from '@/lib/review';
import { overShortTone } from '@/routes/pos/shiftSummary';
import {
  Figure,
  NoncashRows,
  PinDaySection,
  ReadError,
  ReviewItemCard,
  ReviewStatusBadge,
  useFormats,
} from './reviewItems';

/** One business date: the day at a glance (its takings, what is to check, its shifts), then what is to check,
 * the shifts, the cash, the non-cash check and the owners' PIN activity. */
export function DayPage() {
  const { date } = useParams<{ date?: string }>();
  const { locationId, run, ensureFresh } = useOwnerReview();
  const nav = useNavigate();
  const f = useFormats();
  const online = useOnline();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const q = useQuery({
    queryKey: reviewKeys.day(locationId, date ?? null),
    queryFn: () => fetchDayView(locationId, date ?? null),
    refetchInterval: 60_000,
  });
  const d = q.data;
  const markDay = async () => {
    if (!d) return;
    setBusy(true);
    try {
      if (!(await ensureFresh())) return;
      const r = await run('shift.mark_day_reviewed', {
        location_id: locationId,
        business_date: d.business_date,
        digest: d.review.digest,
      });
      if (r) toast('Day marked reviewed');
    } finally {
      setBusy(false);
    }
  };
  if (!d)
    return (
      <div className="d-page">
        {q.error ? (
          <ReadError
            error={q.error}
            hasData={false}
            onRetry={() => void q.refetch()}
            testId="day-error"
          />
        ) : (
          <Loading label="Loading the day" rows={4} />
        )}
      </div>
    );
  const canMark =
    d.review.closed_shifts > 0 &&
    d.review.red_open + d.review.shift_red_open === 0 &&
    d.review.status !== 'reviewed';
  const openItems = d.review.items.filter((i) => i.ack?.state !== 'closed');
  const toCheck = d.review.red_open + d.review.shift_red_open;
  const stillOpen = d.shifts.filter((sh) => sh.status !== 'closed').length;
  const cashOut = Object.entries(d.totals.pay_outs_by_category);
  return (
    <div className="d-page" data-testid="day-page" data-date={d.business_date}>
      <PageHeader
        eyebrow="Owner · Day"
        title={f.date(d.business_date)}
        subtitle={
          d.oldest_unreviewed_date && d.oldest_unreviewed_date !== d.business_date
            ? `Oldest day not reviewed: ${f.date(d.oldest_unreviewed_date)}`
            : undefined
        }
        actions={
          <>
            <IconButton
              label="previous day"
              onClick={() => nav(`/shifts/day/${addDays(d.business_date, -1)}`)}
              data-testid="day-prev"
            >
              ‹
            </IconButton>
            <IconButton
              label="next day"
              disabled={d.business_date >= d.today}
              onClick={() => nav(`/shifts/day/${addDays(d.business_date, 1)}`)}
              data-testid="day-next"
            >
              ›
            </IconButton>
            <ReviewStatusBadge status={d.review.status} />
          </>
        }
      />
      <ReadError error={q.error} hasData onRetry={() => void q.refetch()} testId="day-error" />

      <section className="rv-section" aria-label="The day at a glance">
        <div className="d-stats" data-testid="day-methods">
          {d.totals.by_method.map((m) => (
            <Figure
              key={m.method}
              label={`${m.method} (${m.count})`}
              value={f.money(m.net_minor)}
              hint={
                m.voided_minor + m.refunded_minor > 0
                  ? `of ${f.money(m.captured_minor)} before voids and refunds`
                  : undefined
              }
            />
          ))}
          {d.totals.by_method.length === 0 && (
            <Figure label="Takings" value="—" hint="No payments." />
          )}
        </div>
        <div className="d-stats">
          <Figure
            label="To check"
            value={toCheck}
            tone={toCheck > 0 ? 'danger' : undefined}
            hint={
              [
                d.review.shift_red_open > 0 ? `${d.review.shift_red_open} on its shifts` : null,
                d.review.follow_ups > 0
                  ? `${d.review.follow_ups} follow-up${d.review.follow_ups === 1 ? '' : 's'}`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ') || undefined
            }
          />
          <Figure
            label="Shifts"
            value={d.shifts.length}
            hint={`${d.shifts.length - stillOpen} closed · ${stillOpen} open`}
          />
        </div>
      </section>

      <section className="rv-section" aria-label="To check that day">
        <SectionHead title="To check that day">
          <Link to="/shifts/review" className="d-btn d-btn--sm d-btn--quiet">
            All days
          </Link>
        </SectionHead>
        <div className="d-list" data-testid="day-items">
          {openItems.map((i) => (
            <ReviewItemCard key={i.item_key} item={i} />
          ))}
          {openItems.length === 0 &&
            (d.review.shift_red_open > 0 ? (
              <Notice tone="warning">
                {`${d.review.shift_red_open} item${d.review.shift_red_open === 1 ? '' : 's'} to check on the day's shifts.`}
              </Notice>
            ) : (
              <EmptyState title="Nothing to check for the day itself." />
            ))}
        </div>
        {canMark && (
          <div className="rv-foot">
            <Button
              variant="primary"
              busy={busy}
              disabled={!online}
              onClick={() => void markDay()}
              data-testid="mark-day-reviewed"
            >
              Mark day reviewed
            </Button>
          </div>
        )}
      </section>

      <section className="rv-section" aria-label="Shifts">
        <SectionHead title="Shifts" />
        {d.before_shifts && (
          <Notice tone="info">
            Orders that day, but no shift: from before shifts were switched on.
          </Notice>
        )}
        <div className="d-list" data-testid="day-shifts">
          {d.shifts.map((sh) => (
            <button
              key={sh.id}
              type="button"
              className="d-row d-row--button"
              onClick={() => nav(`/shifts/${sh.id}`)}
              data-testid="day-shift-row"
            >
              <div className="d-row__main">
                <div className="d-row__title">
                  #{sh.seq} · {sh.drawer?.name ?? 'Drawer'} · {sh.cashier.name}
                </div>
                <div className="d-row__meta">
                  {f.at(sh.opened_at)}
                  {sh.closed_at ? ` → ${f.at(sh.closed_at)}` : ' · open'}
                  {sh.close_kind ? ` · ${CLOSE_KIND_LABEL[sh.close_kind] ?? sh.close_kind}` : ''}
                  {sh.adjusted && sh.frozen
                    ? ` · expected at close ${f.money(sh.frozen.expected_minor)}, now ${f.money(sh.expected_now_minor)}`
                    : ''}
                </div>
              </div>
              <div className="rv-side">
                {sh.over_short_minor != null && (
                  <Badge
                    tone={overShortTone(
                      sh.adjusted_figures.over_short_minor ?? sh.over_short_minor,
                      sh.tolerance_minor,
                    )}
                  >
                    {describeOverShort(
                      sh.adjusted_figures.over_short_minor ?? sh.over_short_minor,
                      f.money,
                    )}
                    {sh.adjusted_figures.corrected ? ' · corrected' : ''}
                  </Badge>
                )}
                <ReviewStatusBadge status={sh.status === 'closed' ? sh.review.status : 'open'} />
              </div>
            </button>
          ))}
          {d.shifts.length === 0 && <EmptyState title="No shifts that day." />}
        </div>
      </section>

      <section className="rv-section" aria-label="Cash">
        <SectionHead title="Cash" />
        <div className="d-grid">
          <Surface pad="sm">
            <section className="d-kv" aria-label="The closes" data-testid="day-closes">
              <div className="d-eyebrow">The closes</div>
              <KvRow
                label="Over / short at the closes"
                value={
                  <span className="d-mono d-strong">
                    {describeOverShort(d.totals.over_short_minor, f.money)}
                  </span>
                }
              />
              {d.totals.opening_difference_minor !== 0 && (
                <KvRow
                  label="Opening differences"
                  value={
                    <span className="d-mono">
                      {describeOverShort(d.totals.opening_difference_minor, f.money)}
                    </span>
                  }
                />
              )}
              {d.totals.settled_minor > 0 && (
                <KvRow
                  label="Shortages settled"
                  value={<span className="d-mono">{f.money(d.totals.settled_minor)}</span>}
                />
              )}
              {d.totals.unsettled_minor > 0 && (
                <KvRow
                  label="Shortages unsettled"
                  value={<span className="d-mono">{f.money(d.totals.unsettled_minor)}</span>}
                />
              )}
              {d.totals.corrections > 0 && (
                <KvRow
                  label="Corrections"
                  value={<span className="d-mono">{d.totals.corrections}</span>}
                />
              )}
              {d.totals.late_entries.count > 0 && (
                <KvRow
                  label={`Late entries (${d.totals.late_entries.count})`}
                  value={
                    <span className="d-mono">{f.money(d.totals.late_entries.amount_minor)}</span>
                  }
                />
              )}
              {d.totals.discarded > 0 && (
                <KvRow
                  label="Payments discarded"
                  value={<span className="d-mono">{d.totals.discarded}</span>}
                />
              )}
            </section>
          </Surface>
          <Surface pad="sm">
            <section className="d-kv" aria-label="By person" data-testid="day-people">
              <div className="d-eyebrow">By person</div>
              {d.totals.by_person.map((p) => (
                <KvRow
                  key={p.employee.id}
                  label={`${p.employee.name} (${p.shifts})`}
                  value={
                    <span className="d-mono">
                      {describeOverShort(p.close_over_short_minor, f.money)}
                    </span>
                  }
                />
              ))}
              {d.totals.by_person.length === 0 && <p className="d-muted">No closed shift.</p>}
            </section>
          </Surface>
          <Surface pad="sm">
            <section className="d-kv" aria-label="Cash out">
              <div className="d-eyebrow">Cash out</div>
              {cashOut.map(([k, v]) => (
                <KvRow
                  key={k}
                  label={PAY_OUT_LABELS[k as PayOutCategory] ?? k}
                  value={<span className="d-mono">{f.money(v)}</span>}
                />
              ))}
              {d.totals.removed_to_owner_minor > 0 && (
                <KvRow
                  label="Handed to the owner"
                  value={<span className="d-mono">{f.money(d.totals.removed_to_owner_minor)}</span>}
                />
              )}
              {d.totals.removed_to_safe_minor > 0 && (
                <KvRow
                  label="Into the safe"
                  value={<span className="d-mono">{f.money(d.totals.removed_to_safe_minor)}</span>}
                />
              )}
              {cashOut.length === 0 &&
                d.totals.removed_to_owner_minor <= 0 &&
                d.totals.removed_to_safe_minor <= 0 && (
                  <p className="d-muted">No cash taken out.</p>
                )}
            </section>
          </Surface>
        </div>
      </section>

      <section className="rv-section" aria-label="Non-cash payments">
        <SectionHead title="Non-cash payments">
          <Badge tone={d.noncash.unmarked_count > 0 ? 'warning' : 'neutral'} testId="day-unmarked">
            {d.noncash.unmarked_count} to check · {f.money(d.noncash.unmarked_minor)}
          </Badge>
        </SectionHead>
        <NoncashRows
          rows={d.noncash.rows}
          batch={{
            business_date: d.business_date,
            ids: d.noncash.batch_ids,
            count: d.noncash.batch_count,
            total_minor: d.noncash.batch_total_minor,
          }}
        />
      </section>

      {d.outside_shift && d.outside_shift.count > 0 && (
        <section
          className="rv-section"
          aria-label="Given back outside a shift"
          data-testid="day-outside-shift"
        >
          <SectionHead title="Given back outside a shift">
            <span className="d-help" data-testid="day-outside-total">
              {d.outside_shift.count} · {f.money(d.outside_shift.refunded_minor)}
            </span>
          </SectionHead>
          <div className="d-list">
            {d.outside_shift.rows.map((r) => (
              <div
                key={r.reversal_id}
                className="d-row"
                data-testid={`day-outside-${r.reversal_id}`}
              >
                <div className="d-row__main">
                  <div className="rv-head">
                    <div className="d-row__title">
                      Order {r.order_number} · {r.method}
                      {r.reference_last4 ? ` · ref …${r.reference_last4}` : ' · no reference yet'}
                    </div>
                    <span className="rv-money">{f.money(r.amount_minor)}</span>
                  </div>
                  <div className="d-row__meta">
                    {r.by.name} · “{r.reason}”
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="d-help">
            Money you sent yourself, by GCash or transfer: it never touched a drawer. Check it in
            the non-cash list above like the rest.
          </p>
        </section>
      )}

      {d.pin.map((p) => (
        <PinDaySection key={p.owner.id} state={p} />
      ))}
    </div>
  );
}
